<template>
    <div class="absolute bottom-0 left-0 right-0 pb-8 z-40">
        <!-- Tags Section -->
        <div v-if="videoData.is_audition" class="px-4 mb-3">
            <!-- Expired Badge -->
            <span
                v-if="isBillerExpired"
                class="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 text-white/50 text-xs font-medium px-4 py-2 rounded-full shadow-lg mb-2"
            >
                <Ban class="w-3.5 h-3.5 text-white/40" />
                EXPIRED
            </span>

            <!-- Status Badge -->
            <a
                v-if="isCompleted && !isBillerExpired"
                @click.stop="$emit('watchReleasedSceneClick')"
                :href="route(routePrefix + '.scene.show', videoData.scene_slug)"
                target="_blank"
                class="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-emerald-500/30 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg mb-2"
            >
                <Play class="w-3.5 h-3.5 text-emerald-400" fill="currentColor" />
                RELEASED
            </a>

            <span
                v-if="isProduction"
                class="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-amber-500/30 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg mb-2"
            >
                <Film class="w-3.5 h-3.5 text-amber-400" :stroke-width="2.5" />
                IN PRODUCTION
            </span>

            <span
                v-if="showSponsored"
                class="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-blue-700/30 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg mb-2"
            >
                <Check class="w-3.5 h-3.5 text-sky-400" :stroke-width="3" />
                SPONSORED
            </span>

            <!-- Tags - Only show on active auditions (new/almost) -->
            <div
                v-if="!isBillerExpired && !isFunded && !isProduction && !isCompleted"
                class="flex flex-col items-start gap-2"
            >
                <!-- Expiry FOMO pill — highest priority -->
                <button
                    v-if="expiryFomoConfig"
                    type="button"
                    @click.stop="$emit('nearGoalClick', null)"
                    class="inline-flex items-center gap-1 sm:gap-1.5 text-white text-xs sm:text-sm font-semibold px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer transition-[transform,filter] duration-150 hover:scale-105 hover:brightness-110"
                    :class="expiryFomoConfig.classes"
                >
                    <span class="fomo-fire">🔥</span> {{ expiryFomoConfig.text }}
                </button>

                <!-- Default CTA pill — shows when not yet near goal and no expiry FOMO -->
                <button
                    v-if="!expiryFomoConfig && !videoData.is_near_goal"
                    type="button"
                    @click.stop="$emit('nearGoalClick', null)"
                    class="default-fomo-pill inline-flex items-center gap-1.5 text-white text-xs sm:text-sm font-semibold px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer hover:scale-105 hover:brightness-110 transition-[transform,filter] duration-150"
                >
                    <img :src="STAR_ICON" alt="" class="w-4 h-4 shrink-0 star-pill-spin" />
                    Give stars to fund this video!
                </button>

                <Link
                    v-if="!videoData.is_near_goal"
                    :href="getMainAuditionsUrl(isUserLoggedIn, routePrefix)"
                    class="inline-block bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-1.5 rounded-full transition-colors cursor-pointer"
                >
                    #audition
                </Link>

                <!-- Near-goal pill — shows only when no expiry FOMO -->
                <button
                    v-if="!expiryFomoConfig && videoData.is_near_goal && nearGoalConfig"
                    type="button"
                    @click.stop="$emit('nearGoalClick', nearGoalConfig.remaining)"
                    class="inline-flex items-center gap-1 sm:gap-1.5 text-white text-xs sm:text-sm font-semibold px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer transition-[transform,filter] duration-150 hover:scale-105 hover:brightness-110"
                    :class="nearGoalConfig.classes"
                >
                    <span class="fomo-fire">🔥</span> {{ nearGoalConfig.text }}
                </button>
            </div>
        </div>

        <!-- Caption - Show performer, title and description -->
        <div class="px-4 mb-4 max-w-[calc(100%-5rem)] pr-16">
            <!-- Performer Name with Timestamp -->
            <div v-if="videoData.creator" class="flex items-center gap-2 mb-1">
                <p class="text-white/70 text-sm font-medium drop-shadow-lg">
                    {{ videoData.creator.name }}
                </p>
                <template v-if="videoData.original_created_at">
                    <span class="text-white/30">•</span>
                    <p class="text-white/50 text-xs font-normal drop-shadow-lg">
                        {{ getRelativeTime(videoData.original_created_at) }}
                    </p>
                </template>
            </div>

            <div v-if="!isProductionOrReleased && videoData.expires_at && getExpiresInDays(videoData) >= 0" class="flex items-center gap-1 mb-1 text-white/40 text-xs drop-shadow-lg">
                <Clock class="w-3 h-3 flex-shrink-0" />
                {{ getExpiresInDays(videoData) === 0 ? 'Expires today' : `${getExpiresInDays(videoData)} day${getExpiresInDays(videoData) === 1 ? '' : 's'} remaining` }}
            </div>

            <h2 v-if="videoData.title" class="text-white text-base font-bold drop-shadow-lg line-clamp-2 mb-2">
                {{ videoData.title }}
            </h2>

            <template v-if="videoData.description">
                <p
                    ref="descriptionRef"
                    class="text-white/80 text-sm font-normal drop-shadow-lg leading-relaxed"
                    :class="{ 'line-clamp-3': !descriptionExpanded }"
                >
                    {{ videoData.description }}
                </p>
                <button
                    v-if="(descriptionExpanded || isDescriptionClamped) && videoData.description"
                    type="button"
                    @click.stop="descriptionExpanded = !descriptionExpanded"
                    class="mt-1 text-white/90 text-sm no-underline font-medium hover:text-white drop-shadow-lg  underline-offset-2"
                >
                    {{ descriptionExpanded ? 'Read less' : 'Read more' }}
                </button>
            </template>
        </div>

        <!-- Progress Bar -->
        <div v-if="!shouldVideoBeLocked" class="px-4 flex items-center gap-3">
            <button
                @click.stop="$emit('toggleMute')"
                class="flex-shrink-0 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-black/50 transition"
            >
                <VolumeX v-if="isMuted" class="w-5 h-5 text-white drop-shadow-lg" />
                <Volume2 v-else class="w-5 h-5 text-white drop-shadow-lg" />
            </button>
            <div
                ref="progressBarRef"
                @mousedown="handleProgressMouseDown"
                @touchstart="handleProgressTouchStart"
                class="relative flex-1 h-1 bg-white/30 rounded-full overflow-visible cursor-pointer group"
                :class="{ 'h-1.5': isDragging }"
            >
                <!-- Progress Fill -->
                <div
                    class="h-full bg-white rounded-full"
                    :class="{ 'transition-none': isDragging, 'transition-all duration-100': !isDragging }"
                    :style="{ width: `${displayProgress}%` }"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { Volume2, VolumeX, Film, Check, Play, Clock, Ban } from 'lucide-vue-next'
import { Link } from "@inertiajs/vue3"
import { getMainAuditionsUrl } from "@/Helpers/auditions"

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    },
    progress: {
        type: Number,
        default: 0
    },
    isMuted: {
        type: Boolean,
        default: false
    },
    shouldVideoBeLocked: {
        type: Boolean,
        default: false
    },
})

const emit = defineEmits(['toggleMute', 'seek', 'nearGoalClick'])

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const progressBarRef = ref(null)
const descriptionRef = ref(null)
const descriptionExpanded = ref(false)
const isDescriptionClamped = ref(false)
const isDragging = ref(false)
const dragProgress = ref(0) // Local progress while dragging

function checkDescriptionClamped() {
    nextTick(() => {
        if (!descriptionRef.value) {
            isDescriptionClamped.value = false
            return
        }
        isDescriptionClamped.value = descriptionRef.value.scrollHeight > descriptionRef.value.clientHeight
    })
}

watch(() => props.videoData?.description, () => {
    descriptionExpanded.value = false
    checkDescriptionClamped()
})

watch(descriptionExpanded, (expanded) => {
    if (!expanded) checkDescriptionClamped()
})

onMounted(checkDescriptionClamped)

const isCompleted = computed(() => props.videoData.release_status === 2)
const isProduction = computed(() => props.videoData.release_status === 1)
const isFunded = computed(() => props.videoData.star_goal_reached)
const showSponsored = computed(() => props.videoData.star_goal_reached && !props.videoData.release_status)
const isBillerExpired = computed(() => props.videoData.feed_post_status == 3)
const isProductionOrReleased = computed(() => props.videoData.release_status === 1 || props.videoData.release_status === 2)
const expiryFomoConfig = computed(() => {
    if (!props.videoData.expires_at) return null
    const days = Math.ceil((new Date(props.videoData.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    if (days < 0 || days > 7) return null

    if (days === 0) return {
        text: "Don't miss out — ends today!",
        classes: 'bg-rose-600/75 hover:bg-rose-600/90 fomo-glow-rose',
    }
    if (days <= 2) return {
        text: 'This is your last chance',
        classes: 'bg-rose-600/75 hover:bg-rose-600/90 fomo-glow-rose',
    }
    if (days <= 5) return {
        text: 'Going away soon',
        classes: 'bg-orange-500/75 hover:bg-orange-500/90 fomo-glow-orange',
    }
    return {
        text: 'This will leave soon',
        classes: 'bg-amber-500/75 hover:bg-amber-500/90 fomo-glow-amber',
    }
})

const nearGoalConfig = computed(() => {
    const likes = props.videoData.likes ?? 0
    const goal = props.videoData.star_goal

    if (!goal) return null

    const pct = (likes / goal) * 100
    const remaining = goal - likes

    if (pct >= 90) {
        return {
            text: `${remaining} left — be the closer!`,
            classes: 'bg-rose-600/75 hover:bg-rose-600/90 fomo-glow-rose',
            remaining,
        }
    }
    if (pct >= 85) {
        return {
            text: "So close - be the push!",
            classes: 'bg-orange-500/75 hover:bg-orange-500/90 fomo-glow-orange',
            remaining,
        }
    }
    return {
        text: "The community is building!",
        classes: 'bg-amber-500/75 hover:bg-amber-500/90 fomo-glow-amber',
        remaining,
    }
})
const displayProgress = computed(() => isDragging.value ? dragProgress.value : props.progress)

// Mouse events for desktop
const handleProgressMouseDown = (e) => {
    isDragging.value = true
    dragProgress.value = calculateProgress(e.clientX)

    document.addEventListener('mousemove', handleProgressMouseMove)
    document.addEventListener('mouseup', handleProgressMouseUp)
}

const handleProgressMouseMove = (e) => {
    if (isDragging.value) {
        dragProgress.value = calculateProgress(e.clientX)
    }
}

const handleProgressMouseUp = () => {
    if (isDragging.value) {
        emit('seek', dragProgress.value)
        isDragging.value = false
    }
    document.removeEventListener('mousemove', handleProgressMouseMove)
    document.removeEventListener('mouseup', handleProgressMouseUp)
}

// Touch events for mobile
const handleProgressTouchStart = (e) => {
    isDragging.value = true
    const touch = e.touches[0]
    dragProgress.value = calculateProgress(touch.clientX)

    document.addEventListener('touchmove', handleProgressTouchMove, { passive: false })
    document.addEventListener('touchend', handleProgressTouchEnd)
}

const handleProgressTouchMove = (e) => {
    if (isDragging.value) {
        e.preventDefault() // Prevent scrolling while dragging
        const touch = e.touches[0]
        dragProgress.value = calculateProgress(touch.clientX)
    }
}

const handleProgressTouchEnd = () => {
    if (isDragging.value) {
        emit('seek', dragProgress.value)
        isDragging.value = false
    }
    document.removeEventListener('touchmove', handleProgressTouchMove)
    document.removeEventListener('touchend', handleProgressTouchEnd)
}

const calculateProgress = (clientX) => {
    if (!progressBarRef.value) return 0

    const rect = progressBarRef.value.getBoundingClientRect()
    const x = clientX - rect.left
    return Math.max(0, Math.min(100, (x / rect.width) * 100))
}

const getExpiresInDays = (post) => {
    if (!post.expires_at) return null
    return Math.ceil((new Date(post.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
}

const getRelativeTime = (dateString) => {
    const now = new Date()
    const created = new Date(dateString)
    const diffMs = now.getTime() - created.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffMinutes < 60) {
        return diffMinutes <= 1 ? 'Posted just now' : `Posted ${diffMinutes}m ago`
    } else if (diffHours < 24) {
        return `Posted ${diffHours}h ago`
    } else if (diffDays < 7) {
        return `Posted ${diffDays}d ago`
    } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7)
        return `Posted ${weeks}w ago`
    } else {
        const options = created.getFullYear() !== now.getFullYear()
            ? { month: 'short', day: 'numeric', year: 'numeric' }
            : { month: 'short', day: 'numeric' }
        return created.toLocaleDateString('en-US', options)
    }
}
</script>

<style scoped>
@keyframes fomo-fire-flicker {
    0%, 100% { transform: rotate(-8deg) scale(1);    }
    25%       { transform: rotate(6deg)  scale(1.15); }
    50%       { transform: rotate(-4deg) scale(0.95); }
    75%       { transform: rotate(8deg)  scale(1.1);  }
}

.fomo-fire {
    display: inline-block;
    animation: fomo-fire-flicker 1.4s ease-in-out infinite;
    transform-origin: bottom center;
}

@keyframes fomo-glow-pulse-amber {
    0%, 100% { box-shadow: 0 0 6px 1px rgba(245,158,11,0.45); transform: scale(1); }
    50%       { box-shadow: 0 0 14px 4px rgba(245,158,11,0.7); transform: scale(1.03); }
}
@keyframes fomo-glow-pulse-orange {
    0%, 100% { box-shadow: 0 0 6px 1px rgba(249,115,22,0.45); transform: scale(1); }
    50%       { box-shadow: 0 0 14px 4px rgba(249,115,22,0.7); transform: scale(1.03); }
}
@keyframes fomo-glow-pulse-rose {
    0%, 100% { box-shadow: 0 0 6px 1px rgba(225,29,72,0.45); transform: scale(1); }
    50%       { box-shadow: 0 0 14px 4px rgba(225,29,72,0.75); transform: scale(1.03); }
}

.fomo-glow-amber  { animation: fomo-glow-pulse-amber  2.2s ease-in-out infinite; }
.fomo-glow-orange { animation: fomo-glow-pulse-orange 2.2s ease-in-out infinite; }
.fomo-glow-rose   { animation: fomo-glow-pulse-rose   2.2s ease-in-out infinite; }

@keyframes pill-glow-pulse {
    0%, 100% { box-shadow: 0 0 6px 1px rgba(37, 99, 235, 0.4), inset 0 0 0 1px rgba(96, 165, 250, 0.3); }
    50%       { box-shadow: 0 0 14px 4px rgba(37, 99, 235, 0.7), inset 0 0 0 1px rgba(96, 165, 250, 0.6); }
}
.default-fomo-pill {
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.5) 0%, rgba(59, 130, 246, 0.35) 100%);
    border: 1px solid rgba(96, 165, 250, 0.4);
    backdrop-filter: blur(8px);
    animation: pill-glow-pulse 2.5s ease-in-out infinite;
}

@keyframes star-spin {
    0%   { transform: rotate(0deg) scale(1); }
    15%  { transform: rotate(20deg) scale(1.15); }
    30%  { transform: rotate(-15deg) scale(1.1); }
    45%  { transform: rotate(10deg) scale(1.05); }
    60%  { transform: rotate(0deg) scale(1); }
    100% { transform: rotate(0deg) scale(1); }
}
.star-pill-spin {
    animation: star-spin 3s ease-in-out infinite;
    transform-origin: center;
}
</style>
